import { SDK_ERRORS } from '@riz/contracts/sdk';
import { rejectWith } from '../core/errors.ts';
import type { SdkClient } from '../core/client.ts';
import type { SdkPlayer } from '../core/protocol.ts';

export interface UserModule {
  current(): SdkPlayer | null;
  get(): Promise<SdkPlayer>;
  showAuthPrompt(): Promise<SdkPlayer>;
  onChange(handler: (player: SdkPlayer | null) => void): () => void;
  /**
   * Deferred by the decision of 18 August 2026. Kept on the surface so a game
   * can feature-detect it, and so adding it later is not a breaking change.
   */
  getToken(): Promise<never>;
}

export function createUserModule(client: SdkClient): UserModule {
  return {
    current() {
      return client.currentPlayer;
    },

    async get() {
      const capability = client.requireCapability<SdkPlayer>('user.profile', 'Player profile');
      if (capability) return capability;
      const result = await client.call('query', 'player', {});
      return result as unknown as SdkPlayer;
    },

    async showAuthPrompt() {
      const capability = client.requireCapability<SdkPlayer>('user.authPrompt', 'Sign-in prompt');
      if (capability) return capability;
      const result = await client.call('command', 'user/auth-prompt', {});
      const player = result as unknown as SdkPlayer;
      client.emitter.emit('user-changed', player);
      return player;
    },

    onChange(handler) {
      return client.emitter.on<SdkPlayer | null>('user-changed', handler);
    },

    getToken() {
      return rejectWith(
        SDK_ERRORS.capabilityDisabled,
        'Signed identity tokens are not issued yet. Use RizGames.user.current() for display, '
        + 'and the server-side score endpoint with your game API key for backend submissions.',
        { capability: 'user.identityToken' },
      );
    },
  };
}
